import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Trash2,
  RefreshCw,
  BookOpen,
  Search,
  Target,
  Calculator,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  card,
  microLabel,
  sectionTitle,
  accentBarSection,
  bodySecondary,
  motionStaggerChild,
  emptyState,
  iconSize,
} from "../../../../../lib/design-system";
import { cn } from "../../../../../lib/utils";
import {
  listCoursesInProgram,
  assignCourseToProgram,
  unassignCourseFromProgram,
  listAllCoursesAsAdmin,
} from "../../../../../services/program.service";
import CourseForm from "../CourseForm";
import MarksSplitView from "../MarksSplitView";

const CoursesTab = ({ programId, onSelectCourse }) => {
  const [assignedCourses, setAssignedCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [busy, setBusy] = useState(false);
  const [allCoursesUnavailable, setAllCoursesUnavailable] = useState(false);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [splitCourse, setSplitCourse] = useState(null); // {courseId, courseCode, title} for MarksSplitView modal

  const fetchAll = async () => {
    try {
      setLoading(true);
      const assignedData = await listCoursesInProgram(programId);
      setAssignedCourses(assignedData.courses || []);

      // Admin-scoped course list (GET /api/admin/courses/all). Returns the
      // full course list across the institution, not filtered by teacher.
      try {
        const allData = await listAllCoursesAsAdmin({ limit: 200 });
        const courses = Array.isArray(allData)
          ? allData
          : allData.courses || [];
        setAllCourses(courses);
        setAllCoursesUnavailable(false);
      } catch (allErr) {
        console.warn(
          "listAllCoursesAsAdmin failed; assign-existing picker will show a message",
          allErr
        );
        setAllCourses([]);
        setAllCoursesUnavailable(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [programId]);

  const assignedIds = new Set(assignedCourses.map((c) => c._id));

  const handleAssign = async (courseId) => {
    setBusy(true);
    try {
      await assignCourseToProgram(programId, courseId);
      toast.success("Course assigned");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  const handleUnassign = async (courseId) => {
    if (!window.confirm("Unassign this course from the program?")) return;
    setBusy(true);
    try {
      await unassignCourseFromProgram(programId, courseId);
      toast.success("Course unassigned");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || "Unassign failed");
    } finally {
      setBusy(false);
    }
  };

  const unassigned = allCourses.filter((c) => !assignedIds.has(c._id));
  const filteredUnassigned = !searchTerm.trim()
    ? unassigned
    : unassigned.filter((c) => {
        const q = searchTerm.toLowerCase();
        return (
          (c.title || "").toLowerCase().includes(q) ||
          (c.courseCode || "").toLowerCase().includes(q)
        );
      });

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h2 className={cn(sectionTitle, "text-xl sm:text-2xl")}>Courses</h2>
          <div className={cn(accentBarSection, "mt-2")} />
          <p className={cn(bodySecondary, "mt-3 max-w-2xl")}>
            Courses currently assigned to this program. A course can only
            belong to one program at a time — assigning here will overwrite any
            prior assignment.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            className="px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className={iconSize.md} />
          </button>
          <button
            onClick={() => setShowCourseForm(true)}
            className="inline-flex items-center gap-2 bg-accent1 text-white font-semibold px-4 py-2 rounded-sm hover:opacity-90 transition-opacity"
          >
            <Plus className={iconSize.md} />
            New Course
          </button>
        </div>
      </div>

      {loading ? (
        <SkeletonList />
      ) : (
        <>
          {/* Assigned */}
          <section className="mb-10">
            <h3 className={cn(microLabel, "mb-3")}>
              Assigned to this program ({assignedCourses.length})
            </h3>
            {assignedCourses.length === 0 ? (
              <div className={emptyState}>
                <BookOpen className="h-7 w-7 mx-auto text-gray-400 mb-3" />
                <div className="font-bold text-gray-700 dark:text-gray-200">
                  No courses assigned yet
                </div>
                <div className={cn(bodySecondary, "mt-1")}>
                  Pick a course from the list below to assign it.
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {assignedCourses.map((course, i) => (
                  <motion.div
                    key={course._id}
                    {...motionStaggerChild(i, 0)}
                    className={cn(
                      card,
                      "p-4 flex items-center justify-between gap-4 group"
                    )}
                  >
                    <div className="min-w-0">
                      <div className={microLabel}>{course.courseCode}</div>
                      <div className="mt-1 text-sm font-bold text-gray-900 dark:text-white truncate">
                        {course.title}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                      {onSelectCourse && (
                        <button
                          onClick={() =>
                            onSelectCourse({
                              courseId: course._id,
                              courseCode: course.courseCode,
                            })
                          }
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-accent1 text-white hover:opacity-90 font-semibold text-sm transition-opacity"
                          title="Author COs and define CO-PO mapping"
                        >
                          <Target className="h-3.5 w-3.5" /> Manage Outcomes
                        </button>
                      )}
                      <button
                        onClick={() =>
                          setSplitCourse({
                            courseId: course._id,
                            courseCode: course.courseCode,
                            title: course.title,
                          })
                        }
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-accent1 text-accent1 hover:bg-accent2 font-semibold text-sm transition-colors"
                        title="Internal vs external end-sem marks split (NBA Criterion 3/4)"
                      >
                        <Calculator className="h-3.5 w-3.5" /> Marks Split
                      </button>
                      <button
                        onClick={() => handleUnassign(course._id)}
                        disabled={busy}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-red-200 dark:border-red-700 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 font-semibold text-sm transition-colors disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Unassign
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </section>

          {/* Unassigned / available to assign */}
          <section>
            <div className="flex items-center justify-between gap-4 mb-3">
              <h3 className={microLabel}>
                Available to assign ({filteredUnassigned.length})
              </h3>
              <div className="relative max-w-xs flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Filter…"
                  className="w-full pl-9 pr-3 py-1.5 text-sm rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:border-accent1"
                />
              </div>
            </div>
            {allCoursesUnavailable ? (
              <div className="text-sm text-gray-500 dark:text-gray-400 italic py-4">
                Couldn't load the full course list. The{" "}
                <code className="px-1.5 py-0.5 rounded-sm bg-gray-100 dark:bg-gray-800 text-xs">
                  GET /api/courses
                </code>{" "}
                endpoint may currently be scoped to teacher requests. Assign
                via the API for now:{" "}
                <code className="px-1.5 py-0.5 rounded-sm bg-gray-100 dark:bg-gray-800 text-xs">
                  PUT /api/programs/{programId}/courses/:courseId
                </code>
                .
              </div>
            ) : filteredUnassigned.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400 italic py-4">
                {allCourses.length === 0
                  ? "No courses exist in the system yet."
                  : "All matching courses are already assigned."}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredUnassigned.slice(0, 20).map((course, i) => (
                  <motion.div
                    key={course._id}
                    {...motionStaggerChild(i, 0)}
                    className={cn(
                      card,
                      "p-4 flex items-center justify-between gap-4"
                    )}
                  >
                    <div className="min-w-0">
                      <div className={microLabel}>{course.courseCode}</div>
                      <div className="mt-1 text-sm font-bold text-gray-900 dark:text-white truncate">
                        {course.title}
                      </div>
                      {course.program && (
                        <div className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">
                          Currently in another program — will be reassigned.
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleAssign(course._id)}
                      disabled={busy}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-accent1 text-white font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-opacity"
                    >
                      <Plus className="h-3.5 w-3.5" /> Assign
                    </button>
                  </motion.div>
                ))}
                {filteredUnassigned.length > 20 && (
                  <div className="text-xs text-gray-400 italic text-center pt-2">
                    Showing first 20 of {filteredUnassigned.length} — use
                    filter to narrow down.
                  </div>
                )}
              </div>
            )}
          </section>
        </>
      )}

      {showCourseForm && (
        <CourseForm
          programId={programId}
          onClose={(reload) => {
            setShowCourseForm(false);
            if (reload) fetchAll();
          }}
        />
      )}

      {splitCourse && (
        <MarksSplitView
          courseId={splitCourse.courseId}
          courseCode={splitCourse.courseCode}
          courseTitle={splitCourse.title}
          onClose={() => setSplitCourse(null)}
        />
      )}
    </div>
  );
};

const SkeletonList = () => (
  <div className="space-y-2">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className={cn(card, "p-4")}>
        <div className="flex justify-between gap-4">
          <div className="flex-1 space-y-2">
            <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
            <div className="h-4 w-1/2 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
          </div>
          <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);

export default CoursesTab;
